import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

type SheetSide = "left" | "right" | "top" | "bottom";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  side?: SheetSide;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  side = "right",
  title,
  description,
  children,
  className,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="sheet-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-labelledby={title ? "sheet-title" : undefined}
    >
      <div
        ref={panelRef}
        className={cn("sheet", `sheet-${side}`, className)}
      >
        <div className="sheet-header">
          <div className="sheet-header-text">
            {title && <h2 id="sheet-title" className="sheet-title">{title}</h2>}
            {description && <p className="sheet-description">{description}</p>}
          </div>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {children && <div className="sheet-content">{children}</div>}
      </div>
    </div>,
    document.body
  );
}
